import { create } from 'zustand';
import { AdminUser, LoginRequest, ChangePasswordRequest } from '@shared/types/auth';
import { authService } from '../services/auth';

interface AuthState {
  // State
  user: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  changePassword: (passwordData: ChangePasswordRequest) => Promise<void>;
  clearError: () => void;
  checkAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  // Initial state
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,

  // Login action
  login: async (credentials: LoginRequest) => {
    set({ isLoading: true, error: null });

    try {
      const response = await authService.login(credentials);
      
      if (response.success && response.data) {
        console.log('🎯 Login successful, setting state:', response.data.user);
        set({
          user: response.data.user,
          isAuthenticated: true,
          isLoading: false,
          error: null
        });
        console.log('🎯 State updated, isAuthenticated should be true');
      } else {
        throw new Error(response.message || 'Giriş başarısız');
      }
    } catch (error: any) {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: error.message || 'Giriş başarısız oldu'
      });
      throw error;
    }
  },

  // Logout action
  logout: async () => {
    set({ isLoading: true });

    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      });
    }
  },

  // Refresh authentication
  refreshAuth: async () => {
    set({ isLoading: true });

    try {
      // Check if we have stored auth data
      const storedUser = authService.getUserData();
      const hasToken = authService.getAccessToken();

      if (storedUser && hasToken) {
        // Verify token is still valid
        const isValid = await authService.checkAuth();
        
        if (isValid) {
          set({
            user: storedUser,
            isAuthenticated: true,
            isLoading: false,
            error: null
          });
          return;
        }
      }

      // No valid auth data
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      });
    } catch (error: any) {
      console.error('Auth refresh error:', error);
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      });
    }
  },

  // Change password
  changePassword: async (passwordData: ChangePasswordRequest) => {
    set({ isLoading: true, error: null });

    try {
      await authService.changePassword(passwordData);
      set({ isLoading: false });
    } catch (error: any) {
      set({
        isLoading: false,
        error: error.message || 'Şifre değiştirme başarısız oldu'
      });
      throw error;
    }
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },

  // Check authentication status
  checkAuth: async () => {
    // Only check if we have a token, otherwise skip
    const hasToken = localStorage.getItem('gmh_admin_token');
    if (!hasToken) {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      });
      return;
    }
    
    const { refreshAuth } = get();
    await refreshAuth();
  }
}));

// Utility hooks
export const useAuth = () => {
  const { user, isAuthenticated, isLoading, error } = useAuthStore();
  return { user, isAuthenticated, isLoading, error };
};

export const useAuthActions = () => {
  const { login, logout, refreshAuth, changePassword, clearError, checkAuth } = useAuthStore();
  return { login, logout, refreshAuth, changePassword, clearError, checkAuth };
};
